/**
 * Authenticated Layout Route
 * Pathless layout route that wraps all authenticated pages
 * Provides MainLayout, RecordingProvider, and shared navigation
 * Requires authentication via requireAuth guard
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { createFileRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { MainLayout } from '../components/layout/MainLayout'
import { Sidebar, SidebarUserProfile } from '../components/layout/Sidebar'
import { UpcomingSessionsPanel } from '../components/layout/UpcomingSessions'
import { PatientContextPanel } from '../components/recording/PatientContextPanel'
import { TranscriptionRightPanel } from '../components/recording/TranscriptionRightPanel'
import { useLiveTranscriptMirror } from '../hooks/useLiveTranscriptMirror'
import { isMobileCompanionEnabled } from '../config/mobileCompanion'
import { NotepadRightPanel } from '../components/recording/NotepadRightPanel'
import { ClinicalBackgroundPanel } from '../components/recording/ClinicalBackgroundPanel/ClinicalBackgroundPanel'
import { PreapprovedIntakePanel } from '../components/recording/PreapprovedIntakePanel'
import { NotePreviewPanel } from '../components/notes/NotePreviewPanel/NotePreviewPanel'
import { PanelToggleButton } from '../components/common/PanelToggleButton'
import ChartiLogoIcon from '../components/icons/ChartiLogoIcon'
import LogoIconCollapse from '../components/icons/LogoIconCollapse'
import ChartiSymbol from '../components/icons/ChartiSymbol'
import { Icon } from '../components/ui/Icon'
import { SidebarNavIcons, ToolIcons, ArrowIcons, ActionIcons, FileIcons, GeneralIcons } from '../components/ui/Icon/generalIcons'
import { Avatar } from '../components/ui/Avatar/Avatar'
import { requireAuth } from '../router/guards'
import { OPEN_VISIT_PANEL_EVENT } from '../utils/visitPanel'
import { useAuth } from '../hooks/useAuth'
import { useMediaQuery } from '../hooks/useMediaQuery'
import { RecordingProvider, useRecordingContext } from '../contexts/RecordingContext'
import { WidgetProvider } from '../contexts/WidgetContext'
import { BackgroundJobsWidget } from '../components/recording/BackgroundJobsWidget/BackgroundJobsWidget'
import { RecordingWidget } from '../components/recording/RecordingWidget/RecordingWidget'
import { ProcessingWidget } from '../components/recording/ProcessingWidget/ProcessingWidget'
import { useSessions, useSessionStats } from '../hooks/useSessions'
import { sessionService } from '../services/session.service'
import { mediaService } from '../services/media.service'
import { notesService } from '../services/notes.service'
import { NavigationSection } from '../types/navigation.types'
import { EncounterListItem, SessionStatus, SessionType, UpcomingSession, SessionFilters } from '../types/session.types'
import { CreateSessionModal } from '../pages/Sessions/CreateSessionModal/CreateSessionModal'
import { ConfirmationModal } from '../components/ui/ConfirmationModal/ConfirmationModal'
import { PlanStatusBanner } from '../components/plan/PlanStatusBanner'
import { formatLocalDate, formatTimeValue, getDateKey, resolveSessionDate } from '../utils/sessionDate'

/**
 * Horizontally-scrolling tab bar for the right-side panel. When the tab list
 * overflows the panel width (which happens easily with 5+ tabs on a narrow
 * sidebar) it shows ‹ / › scroll arrows on either side, and auto-scrolls the
 * active tab into view whenever the selection changes, so the leftmost tab
 * (`Upcoming`) doesn't quietly disappear behind the overflow on small screens.
 */
interface RightPanelTabBarProps {
  tabs: Array<{ id: string; label: string }>
  activeId: string
  onSelect: (id: string) => void
}

const RightPanelTabBar: React.FC<RightPanelTabBarProps> = ({ tabs, activeId, onSelect }) => {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  // Track whether the tab list overflows in either direction so the arrow
  // buttons render only when there's somewhere to scroll to.
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const update = () => {
      const left = el.scrollLeft
      const max = el.scrollWidth - el.clientWidth
      setCanScrollLeft(left > 1)
      setCanScrollRight(left < max - 1)
    }
    update()
    el.addEventListener('scroll', update, { passive: true })
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null
    ro?.observe(el)
    // Re-measure when the tab set changes (deps below) and on next paint to
    // catch fonts/layout settling.
    const raf = requestAnimationFrame(update)
    return () => {
      el.removeEventListener('scroll', update)
      ro?.disconnect()
      cancelAnimationFrame(raf)
    }
  }, [tabs.length])

  // Keep the active tab visible when selection changes (e.g. after auto-routing
  // to a new view in code, or when the user clicks a tab off-screen).
  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const idx = tabs.findIndex((t) => t.id === activeId)
    if (idx < 0) return
    const child = el.children[idx] as HTMLElement | undefined
    child?.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
  }, [activeId, tabs])

  const scrollBy = (delta: number) => {
    scrollerRef.current?.scrollBy({ left: delta, behavior: 'smooth' })
  }

  const arrowStyle: React.CSSProperties = {
    flexShrink: 0,
    width: 22,
    height: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e4e7ec',
    borderRadius: 999,
    background: '#ffffff',
    color: '#475467',
    fontSize: 14,
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
  }

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        padding: '10px 8px 0',
        flexShrink: 0,
      }}>
      <button
        type="button"
        aria-label="Scroll tabs left"
        onClick={() => scrollBy(-140)}
        disabled={!canScrollLeft}
        style={{ ...arrowStyle, visibility: canScrollLeft || canScrollRight ? 'visible' : 'hidden', opacity: canScrollLeft ? 1 : 0.35 }}>
        ‹
      </button>
      <div
        ref={scrollerRef}
        role="tablist"
        aria-label="Right panel view"
        style={{
          display: 'flex',
          gap: 4,
          flex: 1,
          minWidth: 0,
          overflowX: 'auto',
          scrollbarWidth: 'none',
        }}>
        {tabs.map((tab) => {
          const isActive = activeId === tab.id
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onSelect(tab.id)}
              style={{
                padding: '6px 10px',
                fontSize: 12,
                fontWeight: 500,
                color: isActive ? '#155dee' : '#475467',
                background: isActive ? '#eff4ff' : 'transparent',
                border: isActive ? '1px solid #c7d7fe' : '1px solid transparent',
                borderRadius: 6,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flexShrink: 0,
              }}>
              {tab.label}
            </button>
          )
        })}
      </div>
      <button
        type="button"
        aria-label="Scroll tabs right"
        onClick={() => scrollBy(140)}
        disabled={!canScrollRight}
        style={{ ...arrowStyle, visibility: canScrollLeft || canScrollRight ? 'visible' : 'hidden', opacity: canScrollRight ? 1 : 0.35 }}>
        ›
      </button>
    </div>
  )
}

/**
 * AuthenticatedLayout Component
 * Renders MainLayout with Sidebar, sessions panel, and child route content
 * Manages panel collapse state and dynamic navigation active state
 */
const AuthenticatedLayout = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isLoading, logout } = useAuth()
  const {
    isRecording,
    isFinishing,
    isNoteGenerating,
    isSoapGenerating,
    generatedNote,
    patientInfo,
    previewPatientInfo,
    rightPanelView,
    setRightPanelView,
    remoteRecordingSession,
    reviewingIntakeEncounterId,
    openIntakeReview,
    transcription,
    transcriptSegments,
    wsConnected,
    wsProcessing,
    backgroundJobs,
    activeProcessingEncounterId,
    currentEncounterId,
  } = useRecordingContext()

  // When the user switches between processing tabs, mirror the selected
  // encounter's transcription / generated note in the sidebar so Summary / Note /
  // Transcription always reflect the active patient.
  const activeBackgroundJob =
    activeProcessingEncounterId && activeProcessingEncounterId !== currentEncounterId
      ? backgroundJobs.find((job) => job.encounterId === activeProcessingEncounterId) ?? null
      : null

  // For a *retained* foreground tab (its job has completed and HomePage holds
  // its data in retainedProcessingTabs, which the sidebar can't see), fetch
  // the encounter transcript on demand so the Transcription view doesn't sit
  // on "Connecting…" with an empty placeholder.
  const [fetchedTranscriptByEncounter, setFetchedTranscriptByEncounter] = useState<Record<string, string>>({})
  useEffect(() => {
    const target = activeProcessingEncounterId
    if (!target) return
    if (target === currentEncounterId) return
    if (activeBackgroundJob) return
    if (fetchedTranscriptByEncounter[target] !== undefined) return
    let cancelled = false
    sessionService
      .getTranscript(target)
      .then((text) => {
        if (cancelled) return
        setFetchedTranscriptByEncounter((prev) => ({ ...prev, [target]: text ?? '' }))
      })
      .catch(() => {
        if (cancelled) return
        setFetchedTranscriptByEncounter((prev) => ({ ...prev, [target]: '' }))
      })
    return () => {
      cancelled = true
    }
  }, [activeProcessingEncounterId, currentEncounterId, activeBackgroundJob, fetchedTranscriptByEncounter])

  const fetchedTranscriptForActive =
    activeProcessingEncounterId &&
    activeProcessingEncounterId !== currentEncounterId &&
    !activeBackgroundJob
      ? fetchedTranscriptByEncounter[activeProcessingEncounterId]
      : undefined

  // ── Remote recording mirror ────────────────────────────────────────────
  // `remoteRecordingSession` comes from RecordingContext, which polls once and
  // shares the result, so this panel and the HomePage banner can never
  // disagree about whether another device is recording. That hook filters to
  // genuinely remote devices (ios/android), so on a desktop-only install this
  // is always null and nothing below changes anything the clinician sees.
  //
  // Subscribed only while the Transcription tab is open. The SSE connection is
  // held for the whole consult, so opening one on every desktop that merely
  // KNOWS a phone is recording would multiply idle connections for nothing.
  // Opening the tab loses no history: the server replays every persisted
  // segment on connect before switching to live updates.
  const remoteMirror = useLiveTranscriptMirror(
    remoteRecordingSession?.encounter_id ?? null,
    { enabled: rightPanelView === 'transcription' && !!remoteRecordingSession },
  )

  // A recording on another device shows in THIS tab, not a separate one: the
  // clinician reads the transcript in one place regardless of which device is
  // capturing it, and provenance is surfaced as a dot + tooltip on the panel
  // instead. It sits LAST in the precedence chain, so anything happening on
  // this machine (a background job, a fetched encounter, the local recorder)
  // still wins -- a remote session must never displace what the user is
  // actively working on here.
  const showRemoteMirror =
    isMobileCompanionEnabled() &&
    !!remoteRecordingSession &&
    !activeBackgroundJob &&
    fetchedTranscriptForActive === undefined &&
    !isRecording &&
    !transcription

  const sidebarTranscription = activeBackgroundJob
    ? activeBackgroundJob.transcription
    : fetchedTranscriptForActive !== undefined
      ? fetchedTranscriptForActive
      : showRemoteMirror
        ? remoteMirror.transcript
        : transcription
  const sidebarGeneratedNote = activeBackgroundJob ? activeBackgroundJob.generatedNote : generatedNote
  // "Is something actively transcribing right now?" This USED to be defined as
  // "the local foreground recorder", which was fine while that was the only
  // live source. A mirrored recording is live but NOT local, so the two ideas
  // had to be separated: `sidebarShowsLiveStatus` keeps the local meaning (it
  // gates the local `wsConnected` / `wsProcessing` props, which say nothing
  // about a remote device), and `sidebarIsLive` below is the one that answers
  // whether the panel should present itself as live at all. Collapsing them
  // again would make a healthy mirror render "Connecting…" forever, because
  // the local socket is closed the whole time.
  const sidebarShowsLiveStatus = !activeBackgroundJob && fetchedTranscriptForActive === undefined

  type AppViewportMode = 'desktop' | 'compact' | 'tablet' | 'mobile'

  const getAppViewportMode = (): AppViewportMode => {
    if (typeof document === 'undefined') return 'desktop'

    const viewport = document.documentElement.dataset.appViewport
    return viewport === 'compact' || viewport === 'tablet' || viewport === 'mobile'
      ? viewport
      : 'desktop'
  }

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      navigate({ to: '/login' })
    }
  }, [isLoading, user, navigate])

  // Panel collapse state
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isSessionsPanelCollapsed, setIsSessionsPanelCollapsed] = useState(false)
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isMobileRightPanelOpen, setIsMobileRightPanelOpen] = useState(false);
  const [appViewportMode, setAppViewportMode] = useState<AppViewportMode>(() => getAppViewportMode())
  const sidebarAutomaticallyCollapsedRef = useRef(false)
  const hasRoomForFullSidebar = useMediaQuery('(min-width: 1280px)')

  useEffect(() => {
    const open = () => { setIsMobileSidebarOpen(false); setIsMobileRightPanelOpen(true); }
    window.addEventListener(OPEN_VISIT_PANEL_EVENT, open)
    return () => window.removeEventListener(OPEN_VISIT_PANEL_EVENT, open)
  }, [])

  // Close mobile panels on route change
  useEffect(() => {
    setIsMobileSidebarOpen(false);
    setIsMobileRightPanelOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const root = document.documentElement
    const syncViewportMode = () => {
      setAppViewportMode(getAppViewportMode())
    }

    syncViewportMode()

    const observer = new MutationObserver((records) => {
      if (records.some((record) => record.attributeName === 'data-app-viewport')) {
        syncViewportMode()
      }
    })

    observer.observe(root, {
      attributes: true,
      attributeFilter: ['data-app-viewport'],
    })

    return () => {
      observer.disconnect()
    }
  }, [])

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  const [isCreateModalOpen, setCreateModalOpen] = useState(false)
  const [sessionPendingDelete, setSessionPendingDelete] = useState<UpcomingSession | null>(null)
  const todayStr = useMemo(() => formatLocalDate(new Date()), [])
  const todayWindowStartStr = useMemo(() => {
    const start = new Date()
    start.setDate(start.getDate() - 1)
    return formatLocalDate(start)
  }, [])
  const todayWindowEndStr = useMemo(() => {
    const end = new Date()
    end.setDate(end.getDate() + 1)
    return formatLocalDate(end)
  }, [])
  const sessionFilters: SessionFilters = useMemo(
    () => ({
      fromDate: todayWindowStartStr,
      toDate: todayWindowEndStr,
      limit: 50,
    }),
    [todayWindowEndStr, todayWindowStartStr]
  )

  const { sessions, refetch } = useSessions(sessionFilters)
  const { refetch: refetchStats } = useSessionStats()

  useEffect(() => {
    const handler = () => {
      refetch({ force: true })
      refetchStats()
    }
    window.addEventListener('charti:session-updated', handler)
    return () => window.removeEventListener('charti:session-updated', handler)
  }, [refetch, refetchStats])

  const normalizeSessionType = useCallback((visitType: string): SessionType => {
    const normalized = visitType.toLowerCase().replace(/\s+/g, '-')
    const allowed: SessionType[] = ['follow-up', 'initial-consultation', 'telehealth', 'routine-checkup', 'urgent-care']
    return allowed.includes(normalized as SessionType) ? (normalized as SessionType) : 'follow-up'
  }, [])

  const getTodaySessionStatus = useCallback((session: EncounterListItem): SessionStatus => {
    const rawStatus = (session.status || '').toLowerCase()
    const terminalStatuses = ['failed', 'cancelled', 'canceled', 'error']
    const processingStatuses = ['processing', 'transcribing', 'diarizing', 'merging', 'summarizing', 'submitted', 'pending', 'in-progress']

    if (session.has_note) return 'completed'
    if (terminalStatuses.includes(rawStatus)) return 'cancelled'
    if (processingStatuses.includes(rawStatus)) return 'in-progress'
    return 'scheduled'
  }, [])

  const todaySessions = useMemo(
    () => sessions.filter((session) => getDateKey(session.scheduled_at || session.created_at) === todayStr),
    [sessions, todayStr],
  )

  const todayStats = useMemo(() => {
    const completed = todaySessions.filter((session) => getTodaySessionStatus(session) === 'completed').length
    const upcoming = todaySessions.filter((session) => {
      const status = getTodaySessionStatus(session)
      return status === 'scheduled' || status === 'in-progress'
    }).length
    return { completed, upcoming }
  }, [getTodaySessionStatus, todaySessions])

  const upcomingSessions = useMemo<UpcomingSession[]>(() => {
    return todaySessions
      .filter((session) => {
        // Parallel Clinic phase-aware filter (Bug 2):
        //   - Hide in-kiosk / not-yet-launched parallel-clinic encounters
        //     entirely (they live in the Parallel Clinic sidebar list, not
        //     the doctor's Upcoming). Showing them with a normal Start
        //     button caused doctors to accidentally trigger a redundant
        //     recording session on patients still answering the kiosk.
        //   - SHOW parallel-clinic encounters whose questionnaire is done
        //     and the doctor is up next, even though their status is
        //     COMPLETED (kiosk_finish flipped it). SessionCard renders the
        //     pink tile + Charti avatar + Review-intake pill via existing
        //     code; pressing Start triggers normal session flow and the
        //     home page auto-opens the PreapprovedIntakeSidebar.
        if (session.parallel_clinic_intake) {
          return session.parallel_clinic_phase === 'awaiting_doctor'
        }
        const status = getTodaySessionStatus(session)
        return status === 'scheduled' || status === 'in-progress'
      })
      .sort((left, right) => {
        const leftTime = resolveSessionDate(left.scheduled_at, left.created_at)?.getTime() ?? 0
        const rightTime = resolveSessionDate(right.scheduled_at, right.created_at)?.getTime() ?? 0
        return leftTime - rightTime
      })
      .map((session) => ({
        id: session.id,
        patient: {
          id: session.patient_id || session.id,
          name: session.patient_name || session.patient_initials,
          initials: session.patient_initials,
        },
        dateLabel: 'Today',
        time: formatTimeValue(session.scheduled_at, session.created_at, [], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        type: normalizeSessionType(session.visit_type),
        status: getTodaySessionStatus(session),
        hasNote: session.has_note,
        hasAudio: session.session_count > 0,
        hasTranscription: !!session.transcription_text,
        // Parallel Clinic, surface the API flags so SessionCard can render
        // the Charti avatar / pink tile modifier and trigger the Review-intake
        // sidebar on awaiting_doctor encounters.
        parallelClinicIntake: !!session.parallel_clinic_intake,
        parallelClinicPhase: session.parallel_clinic_phase ?? null,
      }))
  }, [getTodaySessionStatus, normalizeSessionType, todaySessions])

  // ═══════════════════════════════════════════════════════════════════════════
  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS (React Rules of Hooks)
  // ═══════════════════════════════════════════════════════════════════════════

  // Main navigation with dynamic active state based on current route
  const mainNavigation: NavigationSection[] = useMemo(
    () => [
      {
        id: 'main',
        items: [
          {
            id: 'home',
            label: 'Home',
            icon: <Icon icon={SidebarNavIcons.Home} size="md" />,
            onClick: () => navigate({ to: '/home' }),
            active: location.pathname === '/home',
          },
          {
            id: 'sessions',
            label: 'Sessions',
            icon: <Icon icon={SidebarNavIcons.Sessions} size="md" />,
            onClick: () => navigate({ to: '/sessions' }),
            active: location.pathname === '/sessions',
          },
          {
            id: 'patients',
            label: 'Patients',
            icon: <Icon icon={SidebarNavIcons.Patients} size="md" />,
            onClick: () => navigate({ to: '/patients' }),
            active: location.pathname === '/patients',
          },
          {
            id: 'templates',
            label: 'Templates',
            icon: <Icon icon={SidebarNavIcons.Templates} size="md" />,
            onClick: () => navigate({ to: '/templates' }),
            active: location.pathname === '/templates',
          },
          {
            id: 'forms',
            label: 'Forms',
            icon: <Icon icon={FileIcons.FileCheck} size="md" />,
            onClick: () => navigate({ to: '/forms' }),
            active: location.pathname.startsWith('/forms'),
          },
          {
            id: 'parallel-clinic',
            label: 'Parallel Clinic',
            // Clinic-building glyph reads more on-the-nose than MagicWand
            // for a kiosk-intake / waiting-room feature.
            icon: <Icon icon={GeneralIcons.Building} size="md" />,
            onClick: () => navigate({ to: '/parallel-clinic' }),
            active: location.pathname.startsWith('/parallel-clinic'),
          },
        ],
      },
    ],
    [navigate, location.pathname]
  )

  // Footer navigation with dynamic active state
  const footerNavigation: NavigationSection[] = useMemo(
    () => [
      {
        id: 'footer',
        items: [
          {
            id: 'settings',
            label: 'Settings',
            icon: <Icon icon={ToolIcons.Settings} size="md" />,
            onClick: () => navigate({ to: '/settings' }),
            active: location.pathname === '/settings',
          },
          {
            id: 'support',
            label: 'Support',
            icon: <Icon icon={SidebarNavIcons.Support} size="md" />,
            onClick: () => navigate({ to: '/support' }),
            active: location.pathname === '/support',
          },
        ],
      },
    ],
    [navigate, location.pathname]
  )

  // Event handlers (useCallback hooks)
  const handleUserProfileClick = useCallback(() => {
    // Left empty as it's handled by menuItems now
  }, [])

  const handleLogoutConfirm = useCallback(async () => {
    try {
      setIsLogoutModalOpen(false)
      await logout()
      // Navigation is handled by the useEffect watching user state
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }, [logout])

  const handleUpgrade = useCallback(() => {
    // TODO: Handle upgrade action
  }, [])

  const handleScheduleSession = useCallback(() => {
    setCreateModalOpen(true)
  }, [])

  const handleRequestDeleteSession = useCallback((session: UpcomingSession) => {
    setSessionPendingDelete(session)
  }, [])

  const handleConfirmDeleteSession = useCallback(async () => {
    if (!sessionPendingDelete) return

    try {
      await sessionService.deleteSession(sessionPendingDelete.id)
      setSessionPendingDelete(null)
      refetch()
      refetchStats()
    } catch (error) {
      console.error('Failed to delete session:', error)
      alert('Failed to delete session. Please try again.')
    }
  }, [refetch, refetchStats, sessionPendingDelete])

  const handleDownloadAudio = useCallback(async (session: UpcomingSession) => {
    try {
      const assets = await mediaService.listByEncounter(session.id)
      const audioAsset = assets.find((a) => a.asset_type === 'audio')
      if (!audioAsset) return
      const { download_url, filename } = await mediaService.getDownloadUrl(audioAsset.id)
      const a = document.createElement('a')
      a.href = download_url
      a.download = filename || `recording_${session.id}.webm`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        document.body.removeChild(a)
      }, 100)
    } catch (err) {
      console.error('[Sidebar] Failed to download audio', err)
    }
  }, [])

  const handleDownloadNote = useCallback(async (session: UpcomingSession) => {
    try {
      const note = await notesService.getLatestByEncounter(session.id)
      if (!note) return
      const text = note.rendered_text || JSON.stringify(note.note_json, null, 2)
      const blob = new Blob([text], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `note_${session.patient.name || session.id}.txt`
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      setTimeout(() => {
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      }, 100)
    } catch (err) {
      console.error('[Sidebar] Failed to download note', err)
    }
  }, [])

  const handleStartSession = useCallback(
    (session: UpcomingSession) => {
      navigate({
        to: '/home',
        search: {
          sessionId: session.id,
          patientId: session.patient.id,
          patientName: session.patient.name,
          patientInitials: session.patient.initials || '',
          autoStart: true,
        },
      })
    },
    [navigate]
  )

  // Card-body click, navigate to home with patient pre-selected but DO NOT
  // auto-start the recorder. The user lands on the form ready to press Record.
  const handleStageSession = useCallback(
    (session: UpcomingSession) => {
      navigate({
        to: '/home',
        search: {
          sessionId: session.id,
          patientId: session.patient.id,
          patientName: session.patient.name,
          patientInitials: session.patient.initials || '',
        },
      })
    },
    [navigate]
  )

  const handleViewTranscription = useCallback(
    (session: UpcomingSession) => {
      navigate({ to: '/sessions/$sessionId', params: { sessionId: session.id }, search: { tab: 'transcription' } })
    },
    [navigate]
  )

  const handleCreateSession = useCallback(
    async (data: { patientInitials: string; patientName?: string; visitType: string; scheduledAt?: string; visitDate?: string }) => {
      try {
        await sessionService.createSession({
          patient_initials: data.patientInitials,
          patient_name: data.patientName,
          visit_type: data.visitType,
          scheduled_at: data.scheduledAt,
          visit_date: data.visitDate,
        })
        refetch()
        refetchStats()
      } catch (e) {
        console.error('Failed to create session', e)
        alert('Failed to create session. Please try again.')
      }
    },
    [refetch, refetchStats]
  )

  // Determine if sessions panel should be shown based on current route
  const shouldShowSessionsPanel = location.pathname === '/home'
  // At 1280px, the 248px navigation, 320px visit panel and 560px home card
  // fit with room for shell padding. Closing the visit panel also frees room.
  const shouldAutoCollapseSidebar = shouldShowSessionsPanel && !isSessionsPanelCollapsed && appViewportMode === 'compact' && !hasRoomForFullSidebar
  const showCollapsedNavigation = isSidebarCollapsed && appViewportMode !== 'mobile' && appViewportMode !== 'tablet'

  useEffect(() => {
    if (shouldAutoCollapseSidebar) {
      sidebarAutomaticallyCollapsedRef.current = true
      setIsSidebarCollapsed(true)
    } else if (sidebarAutomaticallyCollapsedRef.current) {
      sidebarAutomaticallyCollapsedRef.current = false
      setIsSidebarCollapsed(false)
    }
  }, [shouldAutoCollapseSidebar])

  const handleSidebarToggle = useCallback(() => {
    sidebarAutomaticallyCollapsedRef.current = false
    setIsSidebarCollapsed(prev => !prev)
  }, [])

  // ═══════════════════════════════════════════════════════════════════════════
  // EARLY RETURN FOR LOADING STATE (safe now - all hooks called above)
  // ═══════════════════════════════════════════════════════════════════════════
  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontSize: '1.125rem',
          color: '#64748b',
        }}>
        Loading...
      </div>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // NON-HOOK CODE (safe to be after early return)
  // ═══════════════════════════════════════════════════════════════════════════

  // Trial information (TODO: Replace with real data from user subscription)
  const trialInfo = {
    daysLeft: 14,
    totalDays: 30,
  }

  // Right panel: tabbed view switching between Upcoming / Patient Summary /
  // Transcription / Notepad / Clinical Background / Note Preview. Available
  // tabs depend on whether there is active recording or processing context.
  const activePatientInfo = patientInfo ?? previewPatientInfo
  const hasActiveRecordingFlow = isRecording || isFinishing || isNoteGenerating || isSoapGenerating || !!generatedNote
  const shouldShowPreviewPatientPanel = location.pathname === '/home' && !!previewPatientInfo
  const hasPatientContext = !!activePatientInfo
  // Upcoming is always available, even while a recording is active or a note
  // is being generated, so the user can keep an eye on today's schedule
  // without leaving the recording flow. The right-panel tab bar scrolls on
  // narrow widths (see RightPanelTabBar) if overflow becomes an issue.
  const showUpcomingTab = true
  const showNotePreviewTab = !!sidebarGeneratedNote

  // Parallel Clinic, surface the tab whenever an intake review target
  // exists. Two routes set the target: (a) the SessionCard's Review-intake
  // icon calls openIntakeReview(encounterId); (b) the auto-attach effect
  // in RecordingContext sets it when the staged encounter has
  // parallel_clinic_intake=true. Falling back to the active encounter id
  // (currentEncounterId / activeProcessingEncounterId) lets the doctor
  // re-open the panel after navigating around without re-clicking
  // Review-intake.
  const intakeReviewTargetId =
    reviewingIntakeEncounterId
    ?? activeProcessingEncounterId
    ?? currentEncounterId
    ?? null
  // Compare against today's upcoming list so we only flip the tab on for
  // encounters we know are parallel-clinic intakes. A bare encounter id
  // (no flag) won't show the tab, avoids surfacing it on every regular
  // recording.
  const stagedSession = intakeReviewTargetId
    ? upcomingSessions.find((s) => s.id === intakeReviewTargetId) ?? null
    : null
  const hasParallelClinicReview =
    !!reviewingIntakeEncounterId
    || (!!stagedSession?.parallelClinicIntake)

  const availableTabs: Array<{ id: typeof rightPanelView; label: string }> = []
  if (showUpcomingTab) availableTabs.push({ id: 'upcoming', label: 'Upcoming' })
  if (hasParallelClinicReview)
    availableTabs.push({ id: 'parallel-clinic', label: 'Parallel Clinic' })
  availableTabs.push({ id: 'patient-summary', label: 'Patient' })
  availableTabs.push({ id: 'transcription', label: 'Transcription' })
  availableTabs.push({ id: 'clinical-background', label: 'Clinical' })
  if (hasActiveRecordingFlow) availableTabs.push({ id: 'notepad', label: 'Notepad' })
  if (showNotePreviewTab) availableTabs.push({ id: 'note-preview', label: 'Note' })

  const effectiveView: typeof rightPanelView = availableTabs.some((t) => t.id === rightPanelView)
    ? rightPanelView
    : (showUpcomingTab ? 'upcoming' : 'patient-summary')

  const tabBar = (
    <div
      role="tablist"
      aria-label="Right panel view"
      style={{
        display: 'flex',
        gap: 4,
        padding: '10px 12px 0',
        overflowX: 'auto',
        flexShrink: 0,
      }}>
      {availableTabs.map((tab) => {
        const isActive = effectiveView === tab.id
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => setRightPanelView(tab.id)}
            style={{
              padding: '6px 10px',
              fontSize: 12,
              fontWeight: 500,
              color: isActive ? '#155dee' : '#475467',
              background: isActive ? '#eff4ff' : 'transparent',
              border: isActive ? '1px solid #c7d7fe' : '1px solid transparent',
              borderRadius: 6,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}>
            {tab.label}
          </button>
        )
      })}
    </div>
  )

  let panelBody: React.ReactNode = null
  if (effectiveView === 'upcoming') {
    panelBody = (
      <UpcomingSessionsPanel
        stats={todayStats}
        sessions={upcomingSessions}
        onStageSession={handleStageSession}
        onStartSession={handleStartSession}
        onDownloadAudio={handleDownloadAudio}
        onDownloadNote={handleDownloadNote}
        onScheduleSession={handleScheduleSession}
        onViewTranscription={handleViewTranscription}
        onDeleteSession={handleRequestDeleteSession}
        onReviewIntake={(session) => openIntakeReview(session.id)}
      />
    )
  } else if (effectiveView === 'transcription') {
    panelBody = (
      <TranscriptionRightPanel
        transcription={sidebarTranscription || ''}
        // Background-job transcripts are persisted as a joined string only, no
        // per-segment data survives. Structured blocks come either from the
        // live foreground session or, when mirroring, from the remote
        // session's persisted segments (which carry timestamps but no speaker
        // labels, so the panel uses its timestamp-only grouping).
        segments={
          showRemoteMirror
            ? remoteMirror.segments.map((s) => ({
                type: 'partial' as const,
                text: s.text,
                start: s.start_ms / 1000,
                end: s.end_ms / 1000,
                speaker: s.speaker ?? null,
              }))
            : sidebarShowsLiveStatus
              ? transcriptSegments
              : undefined
        }
        // A remote mirror follows its SSE connection, independent of the local mic.
        wsConnected={showRemoteMirror ? remoteMirror.isLive && !remoteMirror.error : sidebarShowsLiveStatus && wsConnected}
        wsProcessing={sidebarShowsLiveStatus && wsProcessing}
        // `sidebarShowsLiveStatus` only means "not a background job and not a
        // fetched transcript" -- it stays true after the socket closes, so on
        // its own it left the panel reading "Connecting…" over a finished
        // transcript. Require an actual in-flight recording as well, so the
        // status line disappears once the session ends rather than implying
        // the app is still trying to attach.
        isLive={
          showRemoteMirror
            ? remoteMirror.isLive
            : sidebarShowsLiveStatus && (isRecording || isFinishing)
        }
        remoteDevice={
          showRemoteMirror && remoteRecordingSession
            ? {
                kind: remoteRecordingSession.device_kind ?? 'web',
                label: remoteRecordingSession.device_label,
              }
            : undefined
        }
      />
    )
  } else if (effectiveView === 'notepad') {
    panelBody = <NotepadRightPanel />
  } else if (effectiveView === 'clinical-background') {
    panelBody = (
      <ClinicalBackgroundPanel
        encounterId={activeProcessingEncounterId || currentEncounterId}
      />
    )
  } else if (effectiveView === 'parallel-clinic') {
    // The intake review tab body. Falls back to the active encounter id
    // when no explicit reviewingIntakeEncounterId has been set, so opening
    // the tab on the staged encounter "just works".
    panelBody = (
      <PreapprovedIntakePanel
        encounterId={intakeReviewTargetId}
        contextLabel={stagedSession?.patient?.name ?? undefined}
        onFinalized={() => {
          // Clear the target + drop the tab back to Upcoming. The next
          // intake-review click re-opens it cleanly.
          openIntakeReview(null)
        }}
      />
    )
  } else if (effectiveView === 'note-preview' && sidebarGeneratedNote) {
    panelBody = (
      <div style={{ padding: 16, overflow: 'auto', flex: 1 }}>
        <NotePreviewPanel note={sidebarGeneratedNote} />
      </div>
    )
  } else {
    panelBody = hasPatientContext ? (
      <PatientContextPanel patientInfo={activePatientInfo!} />
    ) : (
      <div style={{ padding: 28, color: '#717680', fontSize: 14 }}>
        Patient summary will appear when a patient is selected.
      </div>
    )
  }

  const sessionsPanel = shouldShowSessionsPanel ? (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {tabBar}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{panelBody}</div>
    </div>
  ) : null

  // Retain reference for shouldShowPreviewPatientPanel (used elsewhere)
  void shouldShowPreviewPatientPanel

  return (
    <>
      <PlanStatusBanner />
      <MainLayout
        mobileBrand={<Link to="/home" aria-label="Charti home"><ChartiLogoIcon width={114} height={36} /></Link>}
        mobileNavigation={[
          { to: '/home', label: 'Home', icon: SidebarNavIcons.Home },
          { to: '/sessions', label: 'Sessions', icon: SidebarNavIcons.Sessions },
          { to: '/patients', label: 'Patients', icon: SidebarNavIcons.Patients },
        ].map(item => <Link key={item.to} to={item.to}
          aria-current={location.pathname === item.to || location.pathname.startsWith(`${item.to}/`) ? 'page' : undefined}>
          <Icon icon={item.icon} size="md" aria-hidden="true" />
          <span>{item.label}</span>
        </Link>)}
        mobileMoreActive={!['/home', '/sessions', '/patients'].some(path => location.pathname === path || location.pathname.startsWith(`${path}/`))}
        isSidebarCollapsed={isSidebarCollapsed}
        isRightPanelCollapsed={isSessionsPanelCollapsed}
        hideSessionsPanel={!shouldShowSessionsPanel}
        isMobileSidebarOpen={isMobileSidebarOpen}
        onToggleMobileSidebar={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
        isMobileRightPanelOpen={isMobileRightPanelOpen}
        onToggleMobileRightPanel={() => setIsMobileRightPanelOpen(!isMobileRightPanelOpen)}
        sidebar={
          <Sidebar
            logo={
              showCollapsedNavigation ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    width: '100%',
                    padding: '8px',
                  }}>
                  <ChartiSymbol width={40} height={40} />
                </div>
              ) : (
                <ChartiLogoIcon width={120} height={40} />
              )
            }
            userProfile={
              <SidebarUserProfile
                name={user ? `${user.firstName} ${user.lastName}` : 'User'}
                email={user?.email || 'user@example.com'}
                avatar={
                  <Avatar name={user ? `${user.firstName} ${user.lastName}` : 'User'} src={user?.avatarUrl} size="sm" />
                }
                menuItems={[
                  {
                    label: 'Log out',
                    icon: <Icon icon={ActionIcons.LogOut} size="sm" />,
                    onClick: () => {
                      setIsLogoutModalOpen(true)
                    },
                    variant: 'danger',
                  },
                ]}
              />
            }
            mainNavigation={mainNavigation}
            footerNavigation={footerNavigation}
            trialInfo={trialInfo}
            onUpgrade={handleUpgrade}
            isCollapsed={showCollapsedNavigation}
          />
        }
        sidebarToggleButton={
          <PanelToggleButton
            direction="left"
            isCollapsed={isSidebarCollapsed}
            onClick={handleSidebarToggle}
            ariaLabel={isSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          />
        }
        sessionsPanel={sessionsPanel}
        sessionsPanelToggleButton={
          shouldShowSessionsPanel ? (
            <PanelToggleButton
              direction="right"
              isCollapsed={isSessionsPanelCollapsed}
              onClick={() => setIsSessionsPanelCollapsed(!isSessionsPanelCollapsed)}
              ariaLabel={isSessionsPanelCollapsed ? 'Expand sessions panel' : 'Collapse sessions panel'}
            />
          ) : null
        }>
        <Outlet />
      </MainLayout>

      <ConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogoutConfirm}
        title="Log out"
        description="Are you sure you want to log out? You will need to sign in again to access your account."
        confirmLabel="Log out"
        variant="destructive"
        icon={<Icon icon={ActionIcons.LogOut} size="lg" />}
      />

      <ConfirmationModal
        isOpen={!!sessionPendingDelete}
        onClose={() => setSessionPendingDelete(null)}
        onConfirm={handleConfirmDeleteSession}
        title="Delete session"
        description={`Are you sure you want to delete the session for ${sessionPendingDelete?.patient.name || 'this patient'}? This action cannot be undone.`}
        confirmLabel="Delete session"
        cancelLabel="Cancel"
        variant="destructive"
        icon={<Icon icon={ActionIcons.Trash} size="lg" />}
      />

      <CreateSessionModal
        isOpen={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onConfirm={handleCreateSession}
      />

      <BackgroundJobsWidget />
      <RecordingWidget />
      <ProcessingWidget />
    </>
  )
}

/**
 * Route Configuration
 * Pathless layout route (/_authenticated) that:
 * - Applies requireAuth guard to all child routes
 * - Wraps children with RecordingProvider for recording state
 * - Renders AuthenticatedLayout with MainLayout structure
 */
export const Route = createFileRoute('/_authenticated')({
  beforeLoad: requireAuth,
  component: () => (
    <WidgetProvider>
      <RecordingProvider>
        <AuthenticatedLayout />
      </RecordingProvider>
    </WidgetProvider>
  ),
})
